import type { ActionSummary, BuilderApi, OwnerCommand, ServingRequest, SiteSummary } from "../api/contract";
import { toBuilderError } from "../api/errors";
import { newActionId } from "./workspaceState";

/** What the owner asked of what visitors see: put an earlier version live, or take the site offline. */
export type ServingChange = { kind: "rollback"; releaseId: string } | { kind: "unpublish" };

/**
 * - `done`: it applied.
 * - `changed`: what visitors see moved since this page loaded; look again before retrying.
 * - `unavailable`: the server no longer keeps that version.
 * - `failed`: it didn't go through, or the answer never arrived.
 */
export type ServingOutcome = "done" | "changed" | "unavailable" | "failed";

export function servingRequest(change: ServingChange, site: Pick<SiteSummary, "servingEpoch">, actionId = newActionId()): ServingRequest {
  return change.kind === "rollback"
    ? { actionId, kind: "rollback", releaseId: change.releaseId, expectedServingEpoch: site.servingEpoch }
    : { actionId, kind: "unpublish", expectedServingEpoch: site.servingEpoch };
}

/**
 * Prepares the change and confirms it with the exact digest the server returned. The owner has
 * already said yes in the interface, so there is no second review. `latestEpoch` reads what visitors
 * see now, to tell a stale page from a refusal.
 */
export async function changeServing(
  api: BuilderApi,
  siteId: string,
  request: ServingRequest,
  latestEpoch: () => Promise<number | undefined>,
): Promise<ServingOutcome> {
  const refused = async (caught: unknown): Promise<ServingOutcome> => {
    if (toBuilderError(caught).outcomeUnknown) return "failed";
    const epoch = await latestEpoch().catch(() => undefined);
    if (epoch !== undefined && epoch !== request.expectedServingEpoch) return "changed";
    return request.kind === "rollback" ? "unavailable" : "failed";
  };
  let prepared: ActionSummary;
  try {
    prepared = await api.preparePublication(siteId, request);
  } catch (caught) {
    return refused(caught);
  }
  if (prepared.state === "succeeded") return "done";
  if (prepared.state !== "awaiting-confirmation" || !prepared.proposalDigest) return "failed";
  try {
    const confirmed = await api.confirmPublication(siteId, prepared.id, prepared.proposalDigest);
    return confirmed.state === "succeeded" ? "done" : "failed";
  } catch (caught) {
    return refused(caught);
  }
}

/**
 * Submits an owner command and, when the server holds it for confirmation (`design.restore`),
 * confirms it with its exact digest. Resolves whether the change applied or is applying.
 */
export async function submitConfirmed(api: BuilderApi, siteId: string, baseDraftId: string, prefix: string, command: OwnerCommand): Promise<boolean> {
  const applied = (action: ActionSummary) => action.state === "queued" || action.state === "running" || action.state === "succeeded";
  try {
    const action = await api.submitAction(siteId, { actionId: `${prefix}-${Date.now().toString(36)}`.slice(0, 64), baseDraftId, command });
    if (action.state === "awaiting-confirmation" && action.proposalDigest) {
      return applied(await api.confirmAction(siteId, action.id, action.proposalDigest));
    }
    return applied(action);
  } catch {
    return false;
  }
}
